import type { ListingSummary } from "@/lib/browse/getHomeBrowseData";
import { normalizeListingCategory } from "@/lib/taxonomy/listingCategories";

export type EditorialCategoryId =
  | "home-decor"
  | "gifts-flowers"
  | "fashion-accessories";

export type EditorialCategoryConfig = {
  id: EditorialCategoryId;
  eyebrow: string;
  heading: string;
  description: string;
  ctaLabel: string;
  href: string;
  videoSrc: string;
  mediaSide: "left" | "right";
  primaryCategorySlugs: string[];
  fallbackCategorySlugs: string[];
};

export type EditorialCategorySelection = EditorialCategoryConfig & {
  listings: ListingSummary[];
  usedFallback: boolean;
};

export const EDITORIAL_CATEGORY_CONFIGS: EditorialCategoryConfig[] = [
  {
    id: "home-decor",
    eyebrow: "For the home",
    heading: "Objects that make a place feel yours.",
    description: "Discover thoughtful pieces from independent shops nearby.",
    ctaLabel: "Shop home & décor",
    href: "/categories/home-decor",
    videoSrc: "/videos/homepage/home-decor.mp4",
    mediaSide: "left",
    primaryCategorySlugs: ["home-decor"],
    fallbackCategorySlugs: ["home-goods-appliances"],
  },
  {
    id: "gifts-flowers",
    eyebrow: "Thoughtful finds",
    heading: "Something special, close to home.",
    description: "Find flowers, gifts and small gestures from local businesses.",
    ctaLabel: "Browse gifts & flowers",
    href: "/categories/flowers-plants",
    videoSrc: "/videos/homepage/gifts-flowers.mp4",
    mediaSide: "right",
    primaryCategorySlugs: ["flowers-plants"],
    fallbackCategorySlugs: ["art-handmade", "books-stationery"],
  },
  {
    id: "fashion-accessories",
    eyebrow: "Local style",
    heading: "Distinctive pieces from nearby boutiques.",
    description:
      "Explore clothing and accessories selected by independent local shops.",
    ctaLabel: "Shop fashion & accessories",
    href: "/categories/clothing-fashion",
    videoSrc: "/videos/homepage/fashion-accessories.mp4",
    mediaSide: "left",
    primaryCategorySlugs: ["clothing-fashion", "jewelry-accessories"],
    fallbackCategorySlugs: [],
  },
];

function getListingKey(listing: ListingSummary) {
  return String(listing?.public_id || listing?.id || "").trim();
}

export function getCanonicalListingCategory(
  listing: ListingSummary
): string | null {
  const candidates = [
    listing?.listing_category,
    listing?.category_info?.slug,
    listing?.category_info?.name,
    listing?.category,
  ];

  for (const candidate of candidates) {
    const canonical = normalizeListingCategory(candidate);
    if (canonical) return canonical;
  }

  return null;
}

function takeMatchingListings({
  listings,
  categorySlugs,
  selectedKeys,
  limit,
}: {
  listings: ListingSummary[];
  categorySlugs: string[];
  selectedKeys: Set<string>;
  limit: number;
}) {
  if (categorySlugs.length === 0 || limit <= 0) return [];

  const allowed = new Set(categorySlugs);
  const selected: ListingSummary[] = [];

  for (const listing of listings) {
    const key = getListingKey(listing);
    if (!key || selectedKeys.has(key)) continue;
    if (!allowed.has(getCanonicalListingCategory(listing) || "")) continue;

    selected.push(listing);
    selectedKeys.add(key);
    if (selected.length >= limit) break;
  }

  return selected;
}

export function selectEditorialCategoryListings(
  listings: ListingSummary[],
  {
    limitPerCategory = 3,
    minimumPrimaryCount = 2,
  }: {
    limitPerCategory?: number;
    minimumPrimaryCount?: number;
  } = {}
): EditorialCategorySelection[] {
  const safeListings = Array.isArray(listings) ? listings : [];
  const safeLimit = Math.min(Math.max(Math.floor(limitPerCategory), 1), 3);
  const safeMinimum = Math.min(
    Math.max(Math.floor(minimumPrimaryCount), 0),
    safeLimit
  );
  const selectedKeys = new Set<string>();

  return EDITORIAL_CATEGORY_CONFIGS.map((config) => {
    const primary = takeMatchingListings({
      listings: safeListings,
      categorySlugs: config.primaryCategorySlugs,
      selectedKeys,
      limit: safeLimit,
    });
    const shouldUseFallback =
      primary.length < safeMinimum && config.fallbackCategorySlugs.length > 0;
    const fallback = shouldUseFallback
      ? takeMatchingListings({
          listings: safeListings,
          categorySlugs: config.fallbackCategorySlugs,
          selectedKeys,
          limit: safeLimit - primary.length,
        })
      : [];

    return {
      ...config,
      listings: [...primary, ...fallback],
      usedFallback: fallback.length > 0,
    };
  });
}
